/**
 * Global search in the top bar (04 §0, HANDOFF §0.2, REQUESTS #162 a): one `GET /search` (contract 0.30.0) over
 * products, versions, components of the current SBOMs, vulnerabilities of open findings and cases, grouped by kind.
 * A hit opens its own page: a product, a version, the version's Components tab filtered to the component, the
 * finding (or Findings filtered by the vulnerability when there are several), the case page itself. ⌘K / Ctrl+K
 * focuses the box from anywhere; on a phone the top bar has a search button that opens the box over the page.
 *
 * ARIA combobox (list autocomplete): arrow keys move the active option, Enter opens it, Escape closes the list.
 */
import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { useSearch as useSearchApi } from "@brevalt/api-client/customer";
import type { SearchHit, SearchResults as Results } from "@brevalt/api-client/customer/model";
import { isApiError, isUuid } from "@brevalt/api-client";

const DEBOUNCE_MS = 250;
/** The API's bounds for `q` after trimming (API-OVERVIEW §3.1). */
const MIN_QUERY = 2;
const MAX_QUERY = 100;
const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [value, ms]);
  return debounced;
}

/** The result list loads when the user first types (not in the initial bundle). */
const SearchResults = lazy(() => import("./search-results"));

/** What the list shows besides the options. */
export type SearchState = "results" | "searching" | "too-many" | "timeout" | "failed" | "empty";

/** A refusal of `GET /search` as a state of the list (never a toast). */
function errorState(e: unknown): SearchState {
  if (!isApiError(e)) return "failed";
  if (e.status === 429) return "too-many";
  // 503 `search-timeout`: the statement took over 3 s (contract 0.30.0 fix).
  if (e.code === "search-timeout") return "timeout";
  return "failed";
}

/** A hit's destination: the path and its search params as an object (the router serialises them, so a component
 * named `123` or `true` stays a string). */
export interface Route {
  to: string;
  search?: Record<string, string>;
}

export interface Option extends Route {
  key: string;
  hit: SearchHit;
}

/** Where a hit goes; null when an id the route needs is missing or isn't a UUID. */
export function routeOf(h: SearchHit): Route | null {
  const ids = (...v: (string | null)[]) => v.every((x) => isUuid(x));
  switch (h.kind) {
    case "product":
      return ids(h.productId) ? { to: `/products/${h.productId ?? ""}` } : null;
    case "version":
      return ids(h.productId, h.versionId)
        ? { to: `/products/${h.productId ?? ""}/versions/${h.versionId ?? ""}` }
        : null;
    case "component":
      return ids(h.productId, h.versionId)
        ? {
            to: `/products/${h.productId ?? ""}/versions/${h.versionId ?? ""}`,
            search: { tab: "components", ...(h.componentName ? { component: h.componentName } : {}) },
          }
        : null;
    case "vulnerability":
      if (h.findingId) return ids(h.findingId) ? { to: `/findings/${h.findingId}` } : null;
      return h.vulnId ? { to: "/findings", search: { vuln: h.vulnId } } : null;
    case "case":
      return ids(h.caseId) ? { to: `/cases/${h.caseId ?? ""}` } : null;
    default:
      return null;
  }
}

/** The hits in the API's group order, each with its route (hits without one are left out). */
export function optionsOf(results: Results | undefined): Option[] {
  return (results?.groups ?? []).flatMap((g) =>
    g.items.flatMap((hit, i) => {
      const route = routeOf(hit);
      return route ? [{ key: `${g.kind}-${String(i)}`, ...route, hit }] : [];
    }),
  );
}

export function GlobalSearch({ sheet = false, onDone }: { sheet?: boolean; onDone?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(sheet);
  const [active, setActive] = useState(0);
  const trimmed = query.trim();
  const q = useDebounced(trimmed, DEBOUNCE_MS);
  const valid = q.length >= MIN_QUERY && q.length <= MAX_QUERY;
  const results = useSearchApi(
    { q },
    { query: { enabled: open && valid, staleTime: 30_000, retry: false, meta: { toastOnError: false } } },
  );

  // ⌘K / Ctrl+K from anywhere (the sheet's box has the focus already).
  useEffect(() => {
    if (sheet) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [sheet]);

  // Results only for the text typed now (never an earlier query's while the next one is on its way): a new `q` is
  // a new query key, whose data is empty until it arrives.
  const fresh = q === trimmed;
  const options = fresh ? optionsOf(results.data) : [];
  const settled = q === trimmed && !results.isFetching;
  const searching = !settled && options.length === 0;
  const showList = open && trimmed.length >= MIN_QUERY;
  const current = options[Math.min(active, options.length - 1)];

  // An Enter pressed before the results for the typed text arrived: act once they are in.
  const pendingEnter = useRef<string | null>(null);
  const first = options[0];
  const chooseRef = useRef<(o: Option | undefined) => void>(() => undefined);
  useEffect(() => {
    if (pendingEnter.current === null || pendingEnter.current !== trimmed || !settled) return;
    chooseRef.current(first);
  }, [settled, trimmed, first]);

  const choose = (o: Option | undefined) => {
    pendingEnter.current = null;
    if (!o) return;
    setOpen(sheet);
    setQuery("");
    input.current?.blur();
    onDone?.();
    void navigate({ to: o.to, ...(o.search ? { search: o.search } : {}) });
  };

  useLayoutEffect(() => {
    chooseRef.current = choose;
  });

  const state: SearchState =
    options.length > 0 ? "results" : searching ? "searching" : results.isError ? errorState(results.error) : "empty";

  return (
    <div className={sheet ? "flex min-h-0 flex-1 flex-col" : "relative"}>
      <label className="relative block">
        <span className="sr-only">{t("shell.search_products_versions_cve_components_or")}</span>
        <Search aria-hidden className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && current ? `${listId}-${current.key}` : undefined}
          value={query}
          maxLength={MAX_QUERY}
          // macOS offered "eve" for "cve" (#162 a): ids and names are not words.
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          enterKeyHint="search"
          // The sheet opens for typing.
          autoFocus={sheet}
          placeholder={t("shell.search_products_cve_case_id")}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onBlur={() => {
            // A pending Enter belongs to this visit of the box: leaving drops it (no late navigation).
            pendingEnter.current = null;
            if (sheet) return;
            // Let a click on an option land first.
            setTimeout(() => {
              // Focused again meanwhile (the user came back to the box): keep the list.
              if (document.activeElement !== input.current) setOpen(false);
            }, 150);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, Math.max(options.length - 1, 0)));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              // Enter acts on what is typed now, never on results of an earlier query: wait until the results
              // for this text are in.
              const typed = e.currentTarget.value.trim();
              if (typed !== q || !settled) pendingEnter.current = typed;
              else choose(current);
            } else if (e.key === "Escape") {
              pendingEnter.current = null;
              // In the sheet, an open list closes first; the next Escape reaches the sheet, which closes.
              if (sheet && (!showList || !query)) return;
              e.stopPropagation();
              setOpen(false);
            }
          }}
          className="h-9 w-full rounded-input border border-border-input bg-surface pl-9 pr-12 text-sm placeholder:text-text-muted max-lg:h-11 max-lg:text-base"
        />
        {!sheet && (
          <kbd
            aria-hidden
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xs border border-border px-1.5 font-mono text-2xs text-text-muted"
          >
            {isMac() ? t("shell.search_shortcut_mac") : t("shell.search_shortcut_other")}
          </kbd>
        )}
      </label>
      {showList && (
        <Suspense fallback={null}>
          <SearchResults
            listId={listId}
            sheet={sheet}
            query={trimmed}
            options={options}
            groups={fresh ? (results.data?.groups ?? []) : []}
            kinds={fresh ? (results.data?.kinds ?? []) : []}
            current={current}
            state={state}
            onChoose={choose}
            onHover={(o) => {
              setActive(options.indexOf(o));
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * The phone's search (#162 a): the top bar has no room for the box below `lg`, so a button opens it over the page,
 * the results below it at full width. A modal sheet: Tab stays inside, the page behind doesn't scroll, Escape from
 * anywhere in it (after closing an open list) or Close returns to the button.
 */
export function MobileSearch() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
  const onSheetKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== "Tab" || !sheet.current) return;
    const focusable = [
      ...sheet.current.querySelectorAll<HTMLElement>("input, button, [href], [tabindex]:not([tabindex='-1'])"),
    ].filter((el) => !el.hasAttribute("disabled"));
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen(true);
        }}
        className="inline-grid size-touch place-items-center rounded-btn text-text-muted hover:bg-surface-2 lg:hidden"
      >
        <Search aria-hidden className="size-5" />
        <span className="sr-only">{t("shell.search_open")}</span>
      </button>
      {open && (
        <div
          ref={sheet}
          role="dialog"
          aria-modal="true"
          onKeyDown={onSheetKey}
          aria-label={t("shell.search_results")}
          data-testid="mobile-search"
          className="fixed inset-0 z-(--z-popover) flex flex-col gap-2 bg-surface p-3 pt-[max(0.75rem,env(safe-area-inset-top))] lg:hidden"
        >
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <GlobalSearch sheet onDone={close} />
            </div>
            <button
              type="button"
              onClick={close}
              className="inline-grid size-touch shrink-0 place-items-center rounded-btn text-text-muted hover:bg-surface-2"
            >
              <X aria-hidden className="size-5" />
              <span className="sr-only">{t("shell.search_close")}</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
